import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import VerifiedIcon from '@mui/icons-material/Verified';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import GppBadIcon from '@mui/icons-material/GppBad';
import { useMySkills, type MechanicSkill } from '@/hooks/useSkills';
import {
  SKILL_CATEGORY_KEYS,
  categoryLabelKey,
  certificateBadge,
  certificateBadgeLabelKey,
  type SkillCategoryKey,
} from '@/utils/skillLevels';

function skillCategory(skill: MechanicSkill): SkillCategoryKey | string {
  return skill.skill?.category ?? 'other';
}

export function OwnSkillsView() {
  const { t } = useTranslation('hr');
  const { t: ts } = useTranslation('settings');
  const { data, isLoading, isError } = useMySkills();

  const grouped = useMemo(() => {
    const rows = data ?? [];
    const byCategory = new Map<string, MechanicSkill[]>();
    for (const key of SKILL_CATEGORY_KEYS) byCategory.set(key, []);
    for (const row of rows) {
      const key = skillCategory(row);
      const list = byCategory.get(key) ?? [];
      list.push(row);
      byCategory.set(key, list);
    }
    return [...byCategory.entries()].filter(([, list]) => list.length > 0);
  }, [data]);

  if (isLoading) {
    return (
      <Stack spacing={1}>
        <Skeleton variant="rectangular" height={48} />
        <Skeleton variant="rectangular" height={48} />
      </Stack>
    );
  }

  if (isError) {
    return <Alert severity="error">{t('skillsMatrix.ownLoadFailed')}</Alert>;
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('skillsMatrix.ownEmpty')}</Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2} data-testid="own-skills-view">
      <Typography variant="body2" color="text.secondary">
        {t('skillsMatrix.ownSubtitle')}
      </Typography>
      {grouped.map(([category, rows]) => (
        <Box key={category}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {ts(categoryLabelKey(category))}
          </Typography>
          <List dense disablePadding>
            {rows.map((row) => {
              const badge = certificateBadge({
                status: row.certificate?.status,
                requiresValidCertificate: row.skill?.requiresValidCertificate ?? false,
              });
              return (
                <ListItem key={row.id} divider data-testid={`own-skill-${row.skillId}`}>
                  <ListItemText
                    primary={row.skill?.name ?? row.skillId}
                    secondary={
                      <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                        {row.wantsToLearn && (
                          <Chip
                            size="small"
                            icon={<SchoolIcon />}
                            label={ts('skills.levels.wantsToLearn')}
                          />
                        )}
                        {badge === 'valid' && (
                          <Chip
                            size="small"
                            icon={<VerifiedIcon />}
                            label={ts(certificateBadgeLabelKey(badge))}
                            color="success"
                            variant="outlined"
                          />
                        )}
                        {badge === 'expiring' && (
                          <Chip
                            size="small"
                            icon={<WarningAmberIcon />}
                            label={ts(certificateBadgeLabelKey(badge))}
                            color="warning"
                            variant="outlined"
                          />
                        )}
                        {badge === 'expired' && (
                          <Chip
                            size="small"
                            icon={<GppBadIcon />}
                            label={ts(certificateBadgeLabelKey(badge))}
                            color="error"
                            variant="outlined"
                          />
                        )}
                        {badge === 'missing' && (
                          <Chip
                            size="small"
                            icon={<GppBadIcon />}
                            label={ts(certificateBadgeLabelKey(badge))}
                            color="error"
                            variant="outlined"
                          />
                        )}
                      </Stack>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>
      ))}
    </Stack>
  );
}
